import { useEffect, useState } from "react";
import { Plus, Upload, Sparkles, Coins, Clock, Wallet, CheckCircle2, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { Loading } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import CoverImage from "../components/CoverImage";

function StatCard({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className="border border-white/10 rounded-xl p-5 bg-card/50">
      <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-[0.15em] mb-3">
        <Icon size={14} strokeWidth={1.5} /> {label}
      </div>
      <p className={`font-display text-3xl tabular-nums ${accent || ""}`}>{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
    </div>
  );
}

export default function CreatorDashboard() {
  const [stories, setStories] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [meta, setMeta] = useState({ categories: [], languages: [] });

  const loadAll = async () => {
    const [s, e, p] = await Promise.all([
      api.get("/creator/stories"),
      api.get("/creator/earnings"),
      api.get("/creator/payouts"),
    ]);
    setStories(s.data);
    setEarnings(e.data);
    setPayouts(p.data);
  };

  useEffect(() => {
    api.get("/meta").then((r) => setMeta(r.data));
    loadAll();
  }, []);

  if (stories === null || earnings === null) return <Loading />;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 animate-fade-up">
      <h1 className="font-display text-4xl mb-8">Creator Studio</h1>
      <Tabs defaultValue="content">
        <TabsList className="bg-card/50 border border-white/10">
          <TabsTrigger value="content" data-testid="tab-content">Content</TabsTrigger>
          <TabsTrigger value="earnings" data-testid="tab-earnings">Earnings</TabsTrigger>
          <TabsTrigger value="payouts" data-testid="tab-payouts">Payouts</TabsTrigger>
        </TabsList>

        <TabsContent value="content" className="mt-6">
          <ContentTab stories={stories} meta={meta} reload={loadAll} />
        </TabsContent>

        <TabsContent value="earnings" className="mt-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard icon={Clock} label="On hold" value={earnings.pending_coins} sub={`Releases after ${earnings.hold_days} days`} accent="text-amber-400" />
            <StatCard icon={Wallet} label="Available" value={earnings.available_coins} sub={`≈ $${earnings.available_usd}`} accent="text-emerald-400" />
            <StatCard icon={Coins} label="Total earned" value={earnings.total_earned} accent="text-gold" />
            <StatCard icon={CheckCircle2} label="Paid out" value={earnings.paid_coins} />
          </div>
          <p className="text-xs text-muted-foreground mb-4">
            Platform commission: {earnings.commission_pct}% recorded at earn-time · Payout rate ${earnings.payout_coin_usd}/coin
          </p>
          <div className="border border-white/10 rounded-xl overflow-hidden" data-testid="earnings-ledger">
            {earnings.ledger.length === 0 ? (
              <p className="text-muted-foreground p-6 text-sm">No earnings yet. Publish premium episodes to start earning.</p>
            ) : earnings.ledger.map((l) => (
              <div key={l.id} className="flex items-center justify-between px-6 py-4 border-b border-white/5 last:border-0">
                <div>
                  <p className="text-sm capitalize">{l.type === "earn" ? "Episode unlock" : "Payout"}</p>
                  <p className="text-xs text-muted-foreground">{new Date(l.created_at).toLocaleString()} · {l.status}</p>
                </div>
                <span className={`tabular-nums font-medium ${l.coins >= 0 ? "text-emerald-400" : "text-primary"}`}>
                  {l.coins >= 0 ? "+" : ""}{l.coins}
                </span>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="payouts" className="mt-6">
          <PayoutsTab earnings={earnings} payouts={payouts} reload={loadAll} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ContentTab({ stories, meta, reload }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", cover_url: "", category: "Drama", language: "English", tags: "", region: "GLOBAL" });
  const [aiBusy, setAiBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [coverBusy, setCoverBusy] = useState(false);

  const aiGenerate = async () => {
    if (!form.title) return toast.error("Add a title first");
    setAiBusy(true);
    try {
      const { data } = await api.post("/creator/ai-blurb", { title: form.title, synopsis: form.description, category: form.category });
      setForm((f) => ({ ...f, description: data.blurb, tags: (data.tags || []).join(", ") }));
      toast.success("AI blurb & tags generated");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setAiBusy(false);
    }
  };

  const createStory = async () => {
    setBusy(true);
    try {
      await api.post("/creator/stories", {
        ...form,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      });
      toast.success("Story created — add episodes, then submit for review");
      setOpen(false);
      setForm({ title: "", description: "", cover_url: "", category: "Drama", language: "English", tags: "", region: "GLOBAL" });
      reload();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const uploadCover = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    setCoverBusy(true);
    try {
      const { data } = await api.post("/creator/story-covers", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((current) => ({ ...current, cover_url: data.url }));
      toast.success("Thumbnail uploaded");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setCoverBusy(false);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <p className="text-sm text-muted-foreground">{stories.length} stories</p>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-story-btn" className="bg-primary hover:bg-primary/90"><Plus size={16} className="mr-2" /> New story</Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-white/10 max-w-lg">
            <DialogHeader><DialogTitle className="font-display text-2xl">Create a story</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Title</Label>
                <Input data-testid="story-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5 bg-black/30 border-white/10" />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Description</Label>
                  <button data-testid="ai-blurb-btn" onClick={aiGenerate} disabled={aiBusy} className="text-xs text-gold hover:text-gold/80 flex items-center gap-1 transition-colors duration-300">
                    {aiBusy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} AI generate
                  </button>
                </div>
                <Textarea data-testid="story-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5 bg-black/30 border-white/10" rows={3} />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Cover image URL</Label>
                <Input data-testid="story-cover" value={form.cover_url} onChange={(e) => setForm({ ...form, cover_url: e.target.value })} placeholder="https://..." className="mt-1.5 bg-black/30 border-white/10" />
                <div className="mt-2 flex flex-wrap items-center gap-3">
                  <label
                    data-testid="story-cover-upload-label"
                    className="inline-flex cursor-pointer items-center gap-2 text-xs text-gold hover:text-gold/80 transition-colors duration-300"
                  >
                    <Upload size={14} strokeWidth={1.5} />
                    {coverBusy ? "Uploading thumbnail..." : "Upload thumbnail"}
                    <input
                      data-testid="story-cover-upload"
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      className="hidden"
                      disabled={coverBusy}
                      onChange={(event) => {
                        const file = event.target.files?.[0];
                        if (file) uploadCover(file);
                        event.target.value = "";
                      }}
                    />
                  </label>
                  <span className="text-xs text-muted-foreground">JPG, PNG, or WebP · max 5MB</span>
                </div>
                {form.cover_url && (
                  <div className="mt-3 h-28 w-20 overflow-hidden rounded bg-secondary" data-testid="story-cover-preview">
                    <CoverImage
                      coverUrl={form.cover_url}
                      alt="Story cover preview"
                      className="h-full w-full object-cover"
                    />
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="story-category" className="mt-1.5 bg-black/30 border-white/10"><SelectValue /></SelectTrigger>
                    <SelectContent>{meta.categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Language</Label>
                  <Select value={form.language} onValueChange={(v) => setForm({ ...form, language: v })}>
                    <SelectTrigger data-testid="story-language" className="mt-1.5 bg-black/30 border-white/10"><SelectValue /></SelectTrigger>
                    <SelectContent>{meta.languages.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Tags (comma separated)</Label>
                <Input data-testid="story-tags" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} className="mt-1.5 bg-black/30 border-white/10" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Target region</Label>
                <Select value={form.region} onValueChange={(v) => setForm({ ...form, region: v })}>
                  <SelectTrigger data-testid="story-region" className="mt-1.5 bg-black/30 border-white/10"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[["GLOBAL", "Global"], ["US", "United States"], ["IN", "India"], ["GB", "United Kingdom"], ["EU", "Europe"], ["AE", "UAE"]].map(([c, l]) => (
                      <SelectItem key={c} value={c}>{l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="story-save" onClick={createStory} disabled={busy || !form.title} className="bg-primary hover:bg-primary/90">{busy ? "Saving..." : "Create story"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-4" data-testid="creator-stories">
        {stories.length === 0 && <p className="text-muted-foreground text-sm py-12 text-center">No stories yet. Create your first story to get started.</p>}
        {stories.map((s) => (
          <StoryRow key={s.id} story={s} reload={reload} />
        ))}
      </div>
    </div>
  );
}

const statusColor = { approved: "text-emerald-400", pending: "text-amber-400", rejected: "text-primary", draft: "text-muted-foreground" };

function StoryRow({ story, reload }) {
  const [epForm, setEpForm] = useState({ title: "", is_premium: true, coin_price: 50 });
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [coverBusy, setCoverBusy] = useState(false);

  const addEpisode = async () => {
    setBusy(true);
    try {
      const order = (story.episodes?.length || 0) + 1;
      await api.post(`/creator/stories/${story.id}/episodes`, { ...epForm, order });
      toast.success("Episode added — upload audio next");
      setOpen(false);
      setEpForm({ title: "", is_premium: true, coin_price: 50 });
      reload();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const uploadAudio = async (epId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    const t = toast.loading("Uploading audio...");
    try {
      await api.post(`/creator/episodes/${epId}/audio`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Audio uploaded & submitted for review", { id: t });
      reload();
    } catch (e) {
      toast.error(apiError(e), { id: t });
    }
  };

  const replaceCover = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    setCoverBusy(true);
    try {
      const { data } = await api.post("/creator/story-covers", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await api.patch(`/creator/stories/${story.id}/cover`, { cover_url: data.url });
      toast.success("Story thumbnail updated");
      reload();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setCoverBusy(false);
    }
  };

  return (
    <div className="border border-white/10 rounded-xl p-5 bg-card/40">
      <div className="flex items-start gap-4">
        <label
          data-testid={`story-cover-change-label-${story.id}`}
          className="relative h-16 w-16 cursor-pointer overflow-hidden rounded bg-secondary shrink-0"
          title="Change story thumbnail"
        >
          {story.cover_url && (
            <CoverImage
              coverUrl={story.cover_url}
              alt=""
              data-testid={`creator-story-cover-${story.id}`}
              className="h-full w-full object-cover"
            />
          )}
          <span className="absolute inset-x-0 bottom-0 bg-black/70 px-1 py-0.5 text-center text-[9px] uppercase tracking-[0.1em] text-white">
            {coverBusy ? "Saving" : "Cover"}
          </span>
          <input
            data-testid={`story-cover-change-${story.id}`}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            disabled={coverBusy}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) replaceCover(file);
              event.target.value = "";
            }}
          />
        </label>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-display text-xl">{story.title}</h3>
            <span className={`text-[10px] uppercase tracking-[0.15em] ${statusColor[story.status]}`}>{story.status}</span>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">{story.category} · {story.episodes?.length || 0} episodes</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid={`add-episode-${story.id}`} variant="outline" size="sm" className="border-white/10 hover:bg-white/5"><Plus size={14} className="mr-1" /> Episode</Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-white/10">
            <DialogHeader><DialogTitle className="font-display text-2xl">Add episode</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Episode title</Label>
                <Input data-testid="episode-title" value={epForm.title} onChange={(e) => setEpForm({ ...epForm, title: e.target.value })} className="mt-1.5 bg-black/30 border-white/10" />
              </div>
              <label className="flex items-center justify-between">
                <span className="text-sm">Premium (paid) episode</span>
                <Switch data-testid="episode-premium" checked={epForm.is_premium} onCheckedChange={(v) => setEpForm({ ...epForm, is_premium: v })} />
              </label>
              {epForm.is_premium && (
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Coin price</Label>
                  <Input data-testid="episode-price" type="number" min={1} value={epForm.coin_price} onChange={(e) => setEpForm({ ...epForm, coin_price: parseInt(e.target.value) || 0 })} className="mt-1.5 bg-black/30 border-white/10" />
                </div>
              )}
            </div>
            <DialogFooter>
              <Button data-testid="episode-save" onClick={addEpisode} disabled={busy || !epForm.title} className="bg-primary hover:bg-primary/90">{busy ? "Adding..." : "Add episode"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {story.episodes?.length > 0 && (
        <div className="mt-4 space-y-2 pl-20">
          {story.episodes.map((ep) => (
            <div key={ep.id} className="flex items-center gap-3 text-sm border-t border-white/5 pt-2">
              <span className="font-mono text-xs text-muted-foreground">{ep.order}</span>
              <span className="flex-1 truncate">{ep.title}</span>
              {ep.is_premium ? <span className="text-gold text-xs flex items-center gap-1"><Coins size={11} />{ep.coin_price}</span> : <span className="text-xs text-muted-foreground">Free</span>}
              <span className={`text-[10px] uppercase tracking-[0.15em] ${statusColor[ep.status]}`}>{ep.status}</span>
              <label className="cursor-pointer text-muted-foreground hover:text-foreground transition-colors duration-300" title="Upload audio">
                <Upload size={15} strokeWidth={1.5} />
                <input
                  data-testid={`upload-audio-${ep.id}`}
                  type="file"
                  accept="audio/*"
                  className="hidden"
                  onChange={(e) => e.target.files[0] && uploadAudio(ep.id, e.target.files[0])}
                />
              </label>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PayoutsTab({ earnings, payouts, reload }) {
  const [kyc, setKyc] = useState({ legal_name: "", country: "US", payout_method: "bank", account_ref: "" });
  const [amount, setAmount] = useState(earnings.min_payout_coins);
  const [busy, setBusy] = useState(false);
  const needsKyc = !["submitted", "approved"].includes(earnings.kyc_status);

  const submitKyc = async () => {
    setBusy(true);
    try {
      await api.post("/creator/kyc", kyc);
      toast.success("KYC submitted");
      reload();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const requestPayout = async () => {
    setBusy(true);
    try {
      await api.post("/creator/payouts", { coins: parseInt(amount) });
      toast.success("Payout requested");
      reload();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const statusColors = { requested: "text-amber-400", paid: "text-emerald-400", rejected: "text-primary" };

  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <div className="border border-white/10 rounded-xl p-6 bg-card/40">
        <h3 className="font-display text-2xl mb-4">Request a payout</h3>
        {needsKyc ? (
          <>
            <p className="text-sm text-muted-foreground mb-4">Complete KYC verification before requesting a payout.</p>
            <div className="space-y-3">
              <Input data-testid="kyc-name" placeholder="Legal full name" value={kyc.legal_name} onChange={(e) => setKyc({ ...kyc, legal_name: e.target.value })} className="bg-black/30 border-white/10" />
              <Input data-testid="kyc-country" placeholder="Country" value={kyc.country} onChange={(e) => setKyc({ ...kyc, country: e.target.value })} className="bg-black/30 border-white/10" />
              <Input data-testid="kyc-account" placeholder="Payout account reference (IBAN / wallet)" value={kyc.account_ref} onChange={(e) => setKyc({ ...kyc, account_ref: e.target.value })} className="bg-black/30 border-white/10" />
              <Button data-testid="kyc-submit" onClick={submitKyc} disabled={busy || !kyc.legal_name} className="bg-primary hover:bg-primary/90 w-full">Submit KYC</Button>
            </div>
          </>
        ) : (
          <>
            <p className="text-sm text-muted-foreground mb-1">Available: <span className="text-emerald-400">{earnings.available_coins} coins</span> (≈ ${earnings.available_usd})</p>
            <p className="text-xs text-muted-foreground mb-4">Minimum payout: {earnings.min_payout_coins} coins · KYC: {earnings.kyc_status}</p>
            <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Coins to withdraw</Label>
            <Input data-testid="payout-amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1.5 mb-4 bg-black/30 border-white/10" />
            <Button data-testid="payout-request" onClick={requestPayout} disabled={busy || earnings.available_coins < earnings.min_payout_coins} className="bg-primary hover:bg-primary/90 w-full">
              <Send size={15} className="mr-2" /> Request payout
            </Button>
          </>
        )}
      </div>

      <div>
        <h3 className="font-display text-2xl mb-4">Payout history</h3>
        <div className="border border-white/10 rounded-xl overflow-hidden" data-testid="payout-history">
          {payouts.length === 0 ? (
            <p className="text-muted-foreground p-6 text-sm">No payout requests yet.</p>
          ) : payouts.map((p) => (
            <div key={p.id} className="flex items-center justify-between px-5 py-4 border-b border-white/5 last:border-0">
              <div>
                <p className="text-sm">{p.coins} coins · ${p.amount_usd}</p>
                <p className="text-xs text-muted-foreground">{new Date(p.requested_at).toLocaleDateString()}</p>
              </div>
              <span className={`text-xs uppercase tracking-[0.15em] ${statusColors[p.status]}`}>{p.status}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
